// Shared production rules: redirects are not canonical pages, and neither
// published database source may silently disappear from a production build.
export function parseRedirects(text) {
  const redirects = new Map();
  for (const line of text.split(/\r?\n/)) {
    const [source, target, status] = line.trim().split(/\s+/);
    if (source?.startsWith('/') && /^30[1278]$/.test(status || '') && !source.includes('*')) {
      redirects.set(source, target);
    }
  }
  return redirects;
}

export function finalRoute(route, redirects) {
  const visited = new Set();
  let result = route;
  while (redirects.has(result)) {
    if (visited.has(result)) throw new Error(`Redirect cycle at ${result}`);
    visited.add(result);
    result = redirects.get(result);
  }
  return result;
}

export function rewriteInternalLinks(html, redirects) {
  return html.replace(/href=(['"])([^'"]+)\1/g, (match, quote, href) => {
    const local = href.replace(/^https:\/\/linkzy\.io(?=\/)/, '');
    const [route] = local.split(/[?#]/);
    const target = finalRoute(route, redirects);
    if (target === route) return match;
    return `href=${quote}${href.startsWith('https://linkzy.io') ? 'https://linkzy.io' : ''}${target}${local.slice(route.length)}${quote}`;
  });
}

export async function fetchPublishedRows(table, select, env, fetcher = fetch) {
  const url = env.VITE_SUPABASE_URL;
  const key = env.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (!url || !key) throw new Error('Prerender requires the public backend URL and publishable key; refusing a partial sitemap.');
  const rows = [];
  const pageSize = 1000;
  for (let offset = 0; ; offset += pageSize) {
    const query = new URLSearchParams({
      ...(table === 'posts' ? { status: 'eq.published' } : { published: 'eq.true' }),
      select, order: 'slug.asc', offset: String(offset), limit: String(pageSize),
    });
    const response = await fetcher(`${url}/rest/v1/${table}?${query}`, {
      headers: { apikey: key, Authorization: `Bearer ${key}` },
      signal: AbortSignal.timeout(60000),
    });
    if (!response.ok) throw new Error(`Failed to load published ${table}: HTTP ${response.status}; refusing a partial sitemap.`);
    const page = await response.json();
    if (!Array.isArray(page)) throw new Error(`Invalid published ${table} response; refusing a partial sitemap.`);
    rows.push(...page);
    if (page.length < pageSize) return rows;
  }
}
const escapeAttr = (value) => String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

// Hosting ignores public/_redirects, so each redirect source gets a tiny static
// page: instant meta refresh (treated as a permanent redirect by Google),
// canonical to the final URL, noindex, and a JS fallback for browsers.
export function renderRedirectStub(target) {
  const url = `https://linkzy.io${target}`;
  const safe = escapeAttr(url);
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<title>Redirecting | Linkzy</title>
<meta name="robots" content="noindex, follow" />
<link rel="canonical" href="${safe}" />
<meta http-equiv="refresh" content="0; url=${safe}" />
<script>location.replace(${JSON.stringify(target)} + location.search + location.hash);</script>
</head>
<body><p>This page has moved to <a href="${safe}">${safe}</a>.</p></body>
</html>
`;
}
